"use client";

import { MetaDot } from "@/components/ui/meta-dot";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface WalletAddressChainBadge {
  id: number;
  name: string;
  iconUrl?: string | null;
}

interface WalletAddressMetaProps {
  address: string;
  usdValue?: string | null;
  chains?: WalletAddressChainBadge[];
  extraChainsCount?: number;
  isLoadingBalance?: boolean;
  className?: string;
  addressClassName?: string;
  usdClassName?: string;
}

function truncateAddress(address: string) {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function WalletAddressMeta({
  address,
  usdValue,
  chains = [],
  extraChainsCount = 0,
  isLoadingBalance = false,
  className,
  addressClassName,
  usdClassName,
}: WalletAddressMetaProps) {
  return (
    <div className={cn("text-ds-text-subtle dark:text-ds-text-disabled flex items-center gap-1 text-xs", className)}>
      <span className={cn("font-mono", addressClassName)} title={address}>
        {truncateAddress(address)}
      </span>
      {usdValue ? (
        <>
          <MetaDot />
          <span className={cn("text-ds-text-secondary font-medium", usdClassName)}>{usdValue}</span>
        </>
      ) : null}
      {chains.length > 0 ? (
        <div className="ml-1 flex items-center -space-x-1">
          {chains.map(chain => {
            if (!chain.iconUrl) return null;

            return (
              <TooltipProvider key={chain.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img
                      src={chain.iconUrl}
                      alt={chain.name}
                      className="dark:border-ds-neutral-800 bg-ds-surface-card h-4 w-4 rounded-full border border-white"
                    />
                  </TooltipTrigger>
                  <TooltipContent>{chain.name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}

          {extraChainsCount > 0 ? <span className="text-ds-text-muted text-3xs ml-1">+{extraChainsCount}</span> : null}
        </div>
      ) : null}
      {isLoadingBalance ? <PixelLoader size="xs" color="blue" className="ml-1" /> : null}
    </div>
  );
}
